import { useState, useEffect, useRef } from "react";
import { usersAPI, authAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import { Camera, Save, User, Briefcase, Award, Phone, Mail, Trash2 } from "lucide-react";

export default function ProfilePage() {
  const { user: authUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [userData, setUserData] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    phone: "",
    bio: "",
    years_experience: "",
    expertise: ""
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await authAPI.me();
      setUserData(response.data);
      setFormData({
        phone: response.data.phone || "",
        bio: response.data.bio || "",
        years_experience: response.data.years_experience || "",
        expertise: response.data.expertise || ""
      });
    } catch (error) {
      console.error("Failed to load user data:", error);
      toast.error("Fehler beim Laden der Profildaten");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSend = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      
      await usersAPI.updateProfile(dataToSend);
      toast.success("Profil erfolgreich gespeichert");
      
      // Refresh user data
      await loadUserData();
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Fehler beim Speichern des Profils");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte wählen Sie eine Bilddatei aus");
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Das Bild darf maximal 2MB groß sein");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      await usersAPI.uploadPhoto(file);
      toast.success("Profilfoto erfolgreich hochgeladen");
      await loadUserData();
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error("Failed to upload photo:", error);
      toast.error("Fehler beim Hochladen des Fotos");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!userData?.profile_photo_url) return;
    
    try {
      await usersAPI.deletePhoto();
      toast.success("Profilfoto entfernt");
      await loadUserData();
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error("Failed to delete photo:", error);
      toast.error("Fehler beim Entfernen des Fotos");
    }
  };

  const getInitials = () => {
    if (!userData) return "?";
    return `${userData.first_name?.[0] || ""}${userData.last_name?.[0] || ""}`.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mein Profil</h1>
        <p className="text-muted-foreground">
          Verwalten Sie Ihre persönlichen Daten und Ihr öffentliches Profil
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Profile Photo Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profilfoto
            </CardTitle>
            <CardDescription>
              Ihr Foto wird in E-Mails an Kunden angezeigt
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <Avatar className="h-32 w-32 ring-4 ring-primary/20">
              <AvatarImage src={userData?.profile_photo_url} alt={userData?.first_name} />
              <AvatarFallback className="text-3xl bg-primary/10">{getInitials()}</AvatarFallback>
            </Avatar>
            
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-upload-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Hochladen...
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Foto ändern
                  </>
                )}
              </Button>
              
              {userData?.profile_photo_url && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDeletePhoto}
                  className="text-destructive hover:text-destructive"
                  data-testid="delete-photo-btn"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground text-center">
              JPG, PNG oder GIF. Max. 2MB.
            </p>
          </CardContent>
        </Card>

        {/* Profile Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Persönliche Informationen
            </CardTitle>
            <CardDescription>
              Diese Informationen werden in Terminbestätigungen an Kunden verwendet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Read-only basic info */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Name</Label>
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{userData?.first_name} {userData?.last_name}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">E-Mail</Label>
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{userData?.email}</span>
                  </div>
                </div>
              </div>

              {/* Editable fields */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefonnummer
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="z.B. +49 123 456789"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="phone-input"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="years_experience" className="flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Jahre Berufserfahrung
                  </Label>
                  <Input
                    id="years_experience"
                    type="number"
                    min="0"
                    max="60"
                    placeholder="z.B. 18"
                    value={formData.years_experience}
                    onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                    data-testid="experience-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expertise" className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Fachgebiete
                  </Label>
                  <Input
                    id="expertise"
                    placeholder="z.B. Photovoltaik, Wärmepumpen"
                    value={formData.expertise}
                    onChange={(e) => setFormData({ ...formData, expertise: e.target.value })}
                    data-testid="expertise-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">
                  Über mich / Key Facts
                </Label>
                <Textarea
                  id="bio"
                  placeholder="Erzählen Sie etwas über sich, das den Kunden einen guten ersten Eindruck vermittelt..."
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  className="resize-none"
                  data-testid="bio-input"
                />
                <p className="text-xs text-muted-foreground">
                  Dieser Text wird in Terminbestätigungs-E-Mails an Kunden angezeigt.
                </p>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Profil speichern
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle>Vorschau: So sehen Kunden Ihr Profil</CardTitle>
          <CardDescription>
            Diese Informationen werden in Terminbestätigungs-E-Mails angezeigt
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-6 rounded-lg border-l-4 border-amber-500">
            <h3 className="text-amber-800 font-semibold mb-4">Ihr persönlicher Fachberater</h3>
            <div className="flex gap-5 items-start">
              {/* Always show avatar - with photo or initials */}
              <Avatar className="h-24 w-24 ring-2 ring-amber-500">
                <AvatarImage src={userData?.profile_photo_url} />
                <AvatarFallback className="bg-amber-500 text-white text-2xl">{getInitials()}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xl font-bold text-gray-900 mb-2">
                  {userData?.first_name} {userData?.last_name}
                </p>
                {formData.years_experience && (
                  <p className="text-gray-600 mb-1">
                    Seit <strong>{formData.years_experience} Jahren</strong> in der Branche tätig
                  </p>
                )}
                {formData.expertise && (
                  <p className="text-gray-600 mb-2">
                    Experte für: <strong>{formData.expertise}</strong>
                  </p>
                )}
                {formData.bio && (
                  <p className="text-gray-600 italic mb-3">"{formData.bio}"</p>
                )}
                {/* Contact info */}
                {(formData.phone || userData?.email) && (
                  <div className="pt-3 mt-3 border-t border-amber-300 text-sm text-amber-800">
                    {formData.phone && <span>Tel: {formData.phone}</span>}
                    {formData.phone && userData?.email && <span className="mx-2">•</span>}
                    {userData?.email && <span>E-Mail: {userData.email}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
